<script setup lang="ts">
import CommonDataType from '@/components/CommonDataTypes/CommonDataType.vue';
import type { PropertyType } from '@/components/CommonDataTypes/types';
import type { VeeCommonDataTypeFieldProps } from '../types';
import { useField } from 'vee-validate';
import { computed, toRef } from 'vue';
import type { QuantityInputProps } from '../types';
import QuantityField from './QuantityField.vue';
import type { Quantity as QuantityType } from '@ptw-p3/unitsnet-js-ptw';
import { useVeeFormProps } from '../utils';

type Field = PropertyType<QuantityType>;
type Props = Omit<QuantityInputProps, 'modelValue'> & VeeCommonDataTypeFieldProps<Field>;

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<Props>();

const veeProps = useVeeFormProps(props);

const {
  value,
  handleChange,
  handleBlur,
  validate: validatePropertyType,
  setTouched,
  meta,
  errorMessage: errorMessagePropertyType,
} = useField<Field>(
  toRef(() => props.name),
  undefined,
  {
    validateOnValueUpdate: false,
  },
);

const { validate: validateQuantity, errorMessage: errorMessageQuantity } = useField<Field>(
  toRef(() => `${props.name}.value`),
);

const { validate: validateNumber, errorMessage: errorMessageNumber } = useField<number>(
  // we set this to '' as a hack,
  // because otherwise it would create a {props.name}.value.value = {} if value.value.value would be null
  // that would fail the required validation
  toRef(() => (value.value.value ? `${props.name}.value.value` : '')),
);

const errorMessage = computed(
  () => errorMessageNumber.value || errorMessageQuantity.value || errorMessagePropertyType.value,
);

function validate() {
  validatePropertyType();
  validateQuantity();
  validateNumber();
}

function onUpdate(input: Field | undefined) {
  handleChange(input, false);

  if (meta.touched) {
    validate();
  } else {
    setTouched(true);
  }
}

function onBlur(event: Event) {
  handleBlur(event);
  validate();
}

const commonDataTypeBindings = computed(() => ({
  props: {
    modelValue: value.value,
  },
  on: {
    'update:modelValue': onUpdate,
    blur: onBlur,
  },
}));
</script>

<template>
  <CommonDataType v-bind="commonDataTypeBindings.props" v-on="commonDataTypeBindings.on">
    <template #default="{ props: templateProps, on }">
      <QuantityField
        v-bind="{
          ...$attrs,
          ...veeProps,
          modelValue: templateProps.modelValue,
        }"
        :errorMessage="errorMessage"
        :displayUnit="value.displayUnit"
        v-on="on"
      />
    </template>
  </CommonDataType>
</template>
